import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { ClaimReferralRequest } from "../contracts/ClaimReferralRequest";
import { ClaimReferralResponse } from "../contracts/ClaimReferralResponse";
import { PartnerTermsResponse } from "../contracts/PartnerTermsResponse";
import { MyReferralPartnerResponse } from "../contracts/MyReferralPartnerResponse";
import { MyPartnerStatus } from "../models/MyPartnerStatus";
import { mapResponseToMyPartnerStatus } from "../mappers/MyPartnerStatusMapper";
import { PartnerTerms } from "../models/PartnerTerms";
import { mapResponseToPartnerTerms } from "../mappers/PartnerTermsMapper";

/**
 * Attributes a stashed referral code to the freshly signed-up instructor.
 * Best-effort — a non-attributable code comes back as attributed: false.
 */
export const claimReferral = createAsyncThunk<
  ClaimReferralResponse,
  ClaimReferralRequest,
  { rejectValue: ApiErrorResponse }
>("referrals/claim", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ClaimReferralResponse>("/referral/claim", request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to claim the referral"));
  }
});

/** The signed-in instructor's own referral-partner standing. */
export const fetchMyPartnerStatus = createAsyncThunk<
  MyPartnerStatus,
  void,
  { rejectValue: ApiErrorResponse }
>("referrals/fetchMyPartnerStatus", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<MyReferralPartnerResponse>("/referral/partner");
    return mapResponseToMyPartnerStatus(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the referral program status"));
  }
});

/** Enrolls the signed-in instructor as a referral partner. Idempotent. */
export const applyAsPartner = createAsyncThunk<
  MyPartnerStatus,
  void,
  { rejectValue: ApiErrorResponse }
>("referrals/applyAsPartner", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<MyReferralPartnerResponse>(
      "/referral/partner/apply"
    );
    return mapResponseToMyPartnerStatus(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to join the referral program"));
  }
});

/** The partner terms page state behind the secret acceptance-link token. */
export const fetchPartnerTerms = createAsyncThunk<
  PartnerTerms,
  string,
  { rejectValue: ApiErrorResponse }
>("referrals/fetchPartnerTerms", async (token, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PartnerTermsResponse>(
      `/partner-terms/${encodeURIComponent(token)}`
    );
    return mapResponseToPartnerTerms(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the partner terms"));
  }
});

/** Records the partner's acceptance of the current partner terms. */
export const acceptPartnerTerms = createAsyncThunk<
  PartnerTerms,
  string,
  { rejectValue: ApiErrorResponse }
>("referrals/acceptPartnerTerms", async (token, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PartnerTermsResponse>(
      `/partner-terms/${encodeURIComponent(token)}/accept`
    );
    return mapResponseToPartnerTerms(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to accept the partner terms"));
  }
});
